import { ref } from 'vue'
import Echo from 'laravel-echo'
import io from 'socket.io-client'
import { parseJwt } from '~/composables/useJwt'

const API_BASE = 'api/notifications'

const unreadCount = ref(0)

/** @type {import('vue').Ref<import('~/types/notifications').INotification[]>} */
const items = ref([])

/** @type {import('vue').Ref<import('~/types/notifications').INotificationMeta>} */
const meta = ref({})

/** @type {import('vue').Ref<import('~/types/notifications').INotification[]>} */
const previewItems = ref([])

/** @type {import('vue').Ref<import('~/types/notifications').INotificationMeta>} */
const previewMeta = ref({})
const loading = ref(false)
const previewLoading = ref(false)
const error = ref('')
const previewError = ref('')

let pollTimer = null
let listenersAttached = false

let echo = null
let realtimeUserId = null
let listFilter = { page: 1, unread: false, category: null }

const onFocus = () => {
  if (!document.hidden) {
    refreshUnreadCount()
  }
}

const onVisibilityChange = () => {
  if (!document.hidden) {
    refreshUnreadCount()
  }
}

function normalizeError(err) {
  return (
    err?.data?.message ||
    err?.data?.errors?.title ||
    err?.message ||
    'Не удалось загрузить уведомления'
  )
}

function parseApiUrl(value) {
  if (!value) return null

  try {
    return new URL(value)
  } catch {
    return null
  }
}

function applyUnreadCount(payload) {
  if (typeof payload?.unreadCount === 'number') {
    unreadCount.value = payload.unreadCount
  }
}

async function fetchList(options = {}) {
  const {
    page = 1,
    count = 20,
    unread = false,
    category = null,
    append = false,
  } = options

  const params = new URLSearchParams({
    page: String(page),
    count: String(count),
  })

  listFilter = { page, unread, category }

  if (unread) params.set('unread', '1')
  if (category) params.set('category', category)

  loading.value = true
  error.value = ''

  try {
    const response = await useApi(`${API_BASE}?${params.toString()}`)
    const data = response?.data ?? {}
    const nextItems = data.items ?? []

    items.value = append ? [...items.value, ...nextItems] : nextItems
    meta.value = data.meta ?? {}
    applyUnreadCount(meta.value)

    return data
  } catch (err) {
    error.value = normalizeError(err)
    return null
  } finally {
    loading.value = false
  }
}

async function fetchPreview(options = {}) {
  const { unread = false } = options
  const params = new URLSearchParams({
    page: '1',
    count: '7',
  })

  if (unread) params.set('unread', '1')

  previewLoading.value = true
  previewError.value = ''

  try {
    const response = await useApi(`${API_BASE}?${params.toString()}`)
    const data = response?.data ?? {}

    previewItems.value = data.items ?? []
    previewMeta.value = data.meta ?? {}
    applyUnreadCount(previewMeta.value)

    return data
  } catch (err) {
    previewError.value = normalizeError(err)
    return null
  } finally {
    previewLoading.value = false
  }
}

async function refreshUnreadCount() {
  try {
    const response = await useApi(`${API_BASE}/unread-count`)
    applyUnreadCount(response?.data)
    return unreadCount.value
  } catch {
    return unreadCount.value
  }
}

function findVisible(id) {
  return [...items.value, ...previewItems.value].filter(item => item.id === id)
}

function setReadState(id, isRead) {
  for (const item of findVisible(id)) {
    item.isRead = isRead
  }
}

function applyRealtimeUnreadCount(value) {
  if (typeof value === 'number') {
    unreadCount.value = value
    meta.value = { ...meta.value, unreadCount: value }
    previewMeta.value = { ...previewMeta.value, unreadCount: value }
  }
}

function onNotificationCreated(payload) {
  if (!payload?.id) return

  const exists =
    items.value.some(item => item.id === payload.id) ||
    previewItems.value.some(item => item.id === payload.id)

  if (exists) return

  const nextUnreadCount = unreadCount.value + 1
  const notification = { ...payload, isRead: false }

  previewItems.value = [notification, ...previewItems.value].slice(0, 7)
  previewMeta.value = {
    ...previewMeta.value,
    unreadCount: nextUnreadCount,
    total_items: (previewMeta.value.total_items ?? 0) + 1,
  }

  const matchesList =
    listFilter.page === 1 &&
    (!listFilter.unread || !notification.isRead) &&
    (!listFilter.category || notification.category === listFilter.category)

  if (items.value.length && matchesList) {
    items.value = [notification, ...items.value]
    meta.value = {
      ...meta.value,
      unreadCount: nextUnreadCount,
      total_items: (meta.value.total_items ?? 0) + 1,
    }
  }

  unreadCount.value = nextUnreadCount
}

function onNotificationRead(payload) {
  if (payload?.all) {
    for (const item of [...items.value, ...previewItems.value]) {
      item.isRead = true
    }
  } else if (Array.isArray(payload?.ids)) {
    for (const id of payload.ids) {
      setReadState(id, true)
    }
  }

  applyRealtimeUnreadCount(payload?.unreadCount)
}

async function markAsRead(id) {
  const targets = findVisible(id)
  const wasUnread = targets.some(item => !item.isRead)
  const previousUnread = unreadCount.value

  if (wasUnread) {
    setReadState(id, true)
    unreadCount.value = Math.max(0, unreadCount.value - 1)
  }

  try {
    const response = await useApi(`${API_BASE}/${id}/read`, { method: 'POST' })
    applyUnreadCount(response?.data)
    setReadState(id, true)
    return true
  } catch (err) {
    if (wasUnread) {
      setReadState(id, false)
      unreadCount.value = previousUnread
    }
    throw err
  }
}

async function markBatch(ids) {
  const uniqueIds = [...new Set(ids)]
  const previousUnread = unreadCount.value
  const changed = []

  for (const id of uniqueIds) {
    for (const item of findVisible(id)) {
      if (!item.isRead) {
        changed.push(item)
        item.isRead = true
      }
    }
  }

  if (changed.length) {
    unreadCount.value = Math.max(0, unreadCount.value - changed.length)
  }

  try {
    const response = await useApi(`${API_BASE}/read`, {
      method: 'POST',
      body: { ids: uniqueIds },
    })
    applyUnreadCount(response?.data)
    return response?.data
  } catch (err) {
    for (const item of changed) {
      item.isRead = false
    }
    unreadCount.value = previousUnread
    throw err
  }
}

async function markAllAsRead() {
  const previousUnread = unreadCount.value
  const changed = [...items.value, ...previewItems.value].filter(
    item => !item.isRead
  )

  for (const item of changed) {
    item.isRead = true
  }
  unreadCount.value = 0

  try {
    const response = await useApi(`${API_BASE}/read-all`, { method: 'POST' })
    applyUnreadCount(response?.data)
    return response?.data
  } catch (err) {
    for (const item of changed) {
      item.isRead = false
    }
    unreadCount.value = previousUnread
    throw err
  }
}

function startRealtime() {
  if (!import.meta.client || echo) return

  const token = localStorage.getItem('token')
  const userId = token ? parseJwt(token)?.sub : null

  if (!token || !userId) return

  const config = useRuntimeConfig()
  const publicConfig = config.public
  const apiUrl = parseApiUrl(publicConfig.BASE_URL)
  const scheme =
    publicConfig.ECHO_SCHEME ||
    apiUrl?.protocol?.replace(':', '') ||
    (window.location.protocol === 'https:' ? 'https' : 'http')
  const host = publicConfig.ECHO_HOST || apiUrl?.hostname || window.location.hostname
  const port = publicConfig.ECHO_PORT || '6001'

  try {
    echo = new Echo({
      broadcaster: 'socket.io',
      host: `${scheme}://${host}:${port}`,
      client: io,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    })

    realtimeUserId = String(userId)
    const channel = echo.private(`notifications.${realtimeUserId}`)
    channel.listen('.notification.created', onNotificationCreated)
    channel.listen('.notification.read', onNotificationRead)

    echo.connector?.socket?.on?.('connect', refreshUnreadCount)
    echo.connector?.socket?.on?.('reconnect', refreshUnreadCount)
  } catch {
    echo?.disconnect()
    echo = null
    realtimeUserId = null
  }
}

function stopRealtime() {
  if (echo && realtimeUserId) {
    echo.leave(`notifications.${realtimeUserId}`)
  }

  echo?.disconnect()

  echo = null
  realtimeUserId = null
}

function startPolling() {
  if (pollTimer) return

  refreshUnreadCount()
  startRealtime()
  pollTimer = setInterval(refreshUnreadCount, 60_000)

  if (!listenersAttached && import.meta.client) {
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    listenersAttached = true
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }

  stopRealtime()

  if (listenersAttached && import.meta.client) {
    window.removeEventListener('focus', onFocus)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    listenersAttached = false
  }
}

export const useNotifications = () => ({
  unreadCount,
  items,
  meta,
  previewItems,
  previewMeta,
  loading,
  previewLoading,
  error,
  previewError,
  fetchList,
  fetchPreview,
  refreshUnreadCount,
  markAsRead,
  markBatch,
  markAllAsRead,
  startPolling,
  stopPolling,
})
